/**
 * Composition registry — `(documentKey, documentVersion) → composition factory`.
 *
 * Keys mirror the legacy template registry (`@hyranai/regta-templates`), so a
 * template key resolves to its IR twin and the beta lane can offer exactly the
 * documents that have been ported.
 */
import { forhandlingsframstallningV1 } from './forhandlingsframstallning.js';
import { forhandlingsordning2PartV1, forhandlingsordning3PartV1 } from './forhandlingsordning.js';
import { forhandlingsoverenskommelse2PartV1, forhandlingsoverenskommelse3PartV1, } from './forhandlingsoverenskommelse.js';
import { strandningsmeddelandeV1 } from './strandningsmeddelande.js';
import { lokalhyraV1 } from './lokalhyra.js';
import { bostadshyraV1 } from './bostadshyra.js';
import { studentbostadV1 } from './studentbostad.js';
import { cleanTillaggBostadV1 } from './clean_tillagg_bostad.js';
import { blockhyraV1 } from './blockhyra.js';
import { lagenhetsforteckningV1 } from './lagenhetsforteckning.js';
import { cleanBilagaV1 } from './clean_bilaga.js';
import { garageV1 } from './garage.js';
import { bilplatsV1 } from './bilplats.js';
import { tillaggsavtalHyresgastanpassningV1 } from './tillaggsavtal_hyresgastanpassning.js';
import { tillaggsavtalTidsbestamdHyraV1 } from './tillaggsavtal_tidsbestamd_hyra.js';
import { indexavtalV1 } from './indexavtal.js';
import { cleanTillaggLokalV1 } from './clean_tillagg_lokal.js';
import { andringHyresvillkorV1 } from './andring_hyresvillkor.js';
import { andringHyresvillkorForhandlingsklausulV1 } from './andring_hyresvillkor_forhandlingsklausul.js';
import { villkorsandring54aV1 } from './villkorsandring_54a.js';
import { uppsagningVillkorsandringLokalV1 } from './uppsagning_villkorsandring_lokal.js';
import { uppsagningAvflyttningLokalV1 } from './uppsagning_avflyttning_lokal.js';
import { meddelande18eV1 } from './meddelande_18e.js';
import { godkannande18dV1 } from './godkannande_18d.js';
import { avstaendeBesittningsskyddBostadV1 } from './avstaende_besittningsskydd_bostad.js';
import { avstaendeBesittningsskyddLokalV1 } from './avstaende_besittningsskydd_lokal.js';
import { ansokanAndrahandV1 } from './ansokan_andrahand.js';
import { evakueringV1 } from './evakuering.js';
import { personuppgiftsavtalV1 } from './personuppgiftsavtal.js';
import { personuppgiftsbilagaV1 } from './personuppgiftsbilaga.js';
import { samtyckeSarskildaAvtalV1 } from './samtycke_sarskilda_avtal.js';
import { samtyckeSarskildaBilagaV1 } from './samtycke_sarskilda_bilaga.js';
export const COMPOSITION_REGISTRY = {
    forhandlingsframstallning: { v1: forhandlingsframstallningV1 },
    forhandlingsordning_2part: { v1: forhandlingsordning2PartV1 },
    forhandlingsordning_3part: { v1: forhandlingsordning3PartV1 },
    forhandlingsoverenskommelse_2part: { v1: forhandlingsoverenskommelse2PartV1 },
    forhandlingsoverenskommelse_3part: { v1: forhandlingsoverenskommelse3PartV1 },
    strandningsmeddelande: { v1: strandningsmeddelandeV1 },
    lokalhyra: { v1: lokalhyraV1 },
    bostadshyra: { v1: bostadshyraV1 },
    studentbostad: { v1: studentbostadV1 },
    clean_tillagg_bostad: { v1: cleanTillaggBostadV1 },
    blockhyra: { v1: blockhyraV1 },
    lagenhetsforteckning: { v1: lagenhetsforteckningV1 },
    clean_bilaga: { v1: cleanBilagaV1 },
    garage: { v1: garageV1 },
    bilplats: { v1: bilplatsV1 },
    tillaggsavtal_hyresgastanpassning: { v1: tillaggsavtalHyresgastanpassningV1 },
    tillaggsavtal_tidsbestamd_hyra: { v1: tillaggsavtalTidsbestamdHyraV1 },
    indexavtal: { v1: indexavtalV1 },
    clean_tillagg_lokal: { v1: cleanTillaggLokalV1 },
    andring_hyresvillkor: { v1: andringHyresvillkorV1 },
    andring_hyresvillkor_forhandlingsklausul: { v1: andringHyresvillkorForhandlingsklausulV1 },
    villkorsandring_54a: { v1: villkorsandring54aV1 },
    uppsagning_villkorsandring_lokal: { v1: uppsagningVillkorsandringLokalV1 },
    uppsagning_avflyttning_lokal: { v1: uppsagningAvflyttningLokalV1 },
    meddelande_18e: { v1: meddelande18eV1 },
    godkannande_18d: { v1: godkannande18dV1 },
    avstaende_besittningsskydd_bostad: { v1: avstaendeBesittningsskyddBostadV1 },
    avstaende_besittningsskydd_lokal: { v1: avstaendeBesittningsskyddLokalV1 },
    ansokan_andrahand: { v1: ansokanAndrahandV1 },
    evakuering: { v1: evakueringV1 },
    personuppgiftsavtal: { v1: personuppgiftsavtalV1 },
    personuppgiftsbilaga: { v1: personuppgiftsbilagaV1 },
    samtycke_sarskilda_avtal: { v1: samtyckeSarskildaAvtalV1 },
    samtycke_sarskilda_bilaga: { v1: samtyckeSarskildaBilagaV1 },
};
export function getComposition(documentKey, documentVersion, values = {}) {
    const factory = COMPOSITION_REGISTRY[documentKey]?.[documentVersion];
    return factory ? factory(values) : null;
}
export function hasComposition(documentKey, documentVersion) {
    return Boolean(COMPOSITION_REGISTRY[documentKey]?.[documentVersion]);
}
export function listCompositions() {
    return Object.entries(COMPOSITION_REGISTRY).flatMap(([documentKey, versions]) => Object.keys(versions).map((documentVersion) => ({ documentKey, documentVersion })));
}
//# sourceMappingURL=registry.js.map